import { Link } from "@tanstack/react-router";
import { FileSearch, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { WorkspacePage } from "./PortalAppShell";
import { ClaimsVolumeTrend } from "./ClaimsVolumeTrend";
import { HistoricalFailureModes } from "./HistoricalFailureModes";
import { LiveTop5 } from "./LiveTop5";
import { LiveProblemRecordRegistry } from "./LiveProblemRecords";
import { LiveClaimEvidence } from "./LiveClaimEvidence";
import { DemoInvestigationJourney } from "./DemoInvestigationJourney";

export function ClaimsWorkspace() {
  return (
    <WorkspacePage
      title="Warranty Claims"
      subtitle="Imported repair history, claim volume, and recorded failure descriptions"
    >
      <EvidenceNote title="What this view shows">
        Claims are reported repairs. The trend groups claims by the source repair date; Failure
        Modes ranks the descriptions recorded in those claims. Aggregate views and individual
        evidence have separate eligibility rules; counts need not match. Claim evidence is read-only
        and does not establish a relationship to a Problem Record.
      </EvidenceNote>
      <div className="grid gap-5 xl:grid-cols-3">
        <ClaimsVolumeTrend />
        <HistoricalFailureModes />
      </div>
      <LiveClaimEvidence />
      <DemoInvestigationJourney />
      <Link
        to="/problem-records"
        className="inline-flex rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
      >
        Open Problem Record registry
      </Link>
    </WorkspacePage>
  );
}

export function PortfolioWorkspace() {
  const { tenantContext } = usePortalRuntime();
  return (
    <WorkspacePage
      title="All Programs"
      subtitle={`${tenantContext.tenantName} · program and vehicle-line coverage`}
    >
      <EvidenceNote title="Program catalog is awaiting data">
        A program is a product or vehicle family tracked together. The current source can provide
        ranked vehicle-line evidence, shown below when available. It does not yet supply a complete
        program catalog, units in service, or program-level targets. A Top 5 ranking is a subset,
        not the full portfolio.
      </EvidenceNote>
      <h2 className="text-sm font-semibold">Source-ranked products and vehicle lines</h2>
      <LiveTop5 key="products" initialCategory="products" embedded />
    </WorkspacePage>
  );
}

export function SignalsWorkspace() {
  return (
    <WorkspacePage
      title="Signal Intelligence"
      subtitle="Source-ranked warranty patterns for human review"
    >
      <EvidenceNote title="A pattern is a reason to investigate">
        A signal draws attention to a potential issue. This view uses Broadview's governed problem
        rankings and source labels. A frequent historical repair does not establish a safety hazard,
        current recurrence, or root cause. Automated detection and signal disposition workflows are
        not connected by this UX update.
      </EvidenceNote>
      <LiveTop5 key="problems" initialCategory="problems" embedded />
    </WorkspacePage>
  );
}

export function SuppliersWorkspace() {
  return (
    <WorkspacePage title="Suppliers" subtitle="Supplier evidence and source-provided rankings">
      <EvidenceNote title="Supplier attribution needs source evidence">
        Supplier rankings appear only when the selected Division has an eligible source. A supplier
        directory, confirmed fault attribution, recovery targets, and payment recovery records are
        not yet connected. Any demonstration ranking remains explicitly labeled.
      </EvidenceNote>
      <LiveTop5 key="suppliers" initialCategory="suppliers" embedded />
    </WorkspacePage>
  );
}

export function ClosedLoopWorkspace() {
  return (
    <WorkspacePage
      title="Closed Loop"
      subtitle="Follow the issue from review through an evidence-supported resolution"
    >
      <EvidenceNote title="Current case status is available; detailed workflow is awaiting evidence">
        Closed-loop work documents the problem, contains its effects, establishes a cause, verifies
        a corrective action, and checks that the issue stays resolved. The current live view
        provides the record summary and status. Stage progress, 8D approvals, PFMEA changes,
        effectiveness results, and audit history require additional governed backend connections.
      </EvidenceNote>
      <LiveProblemRecordRegistry embedded />
      <DemoInvestigationJourney />
    </WorkspacePage>
  );
}

export function ComplianceWorkspace() {
  const requirements = [
    [
      "Controlled evidence",
      "Who created the evidence, which version was used, and which case it supports.",
    ],
    [
      "Traceable decisions",
      "The authorized decision maker, the decision date, the rationale, and the evidence considered.",
    ],
    [
      "Corrective-action verification",
      "Evidence that an approved action addressed the issue and remained effective.",
    ],
    [
      "Approved compliance assessment",
      "An authorized review linking the evidence to the applicable requirement.",
    ],
  ];
  return (
    <WorkspacePage
      title="IATF & ISO 9001"
      subtitle="Compliance evidence readiness · no compliance verdict has been established"
    >
      <EvidenceNote title="Compliance assessment is awaiting governed evidence">
        The imported claims can help identify patterns for investigation. Claim history alone cannot
        establish compliance with IATF 16949 or ISO 9001. This workspace outlines the evidence
        needed; it does not provide a certification, pass/fail verdict, or percentage score.
      </EvidenceNote>
      <div className="grid gap-4 sm:grid-cols-2">
        {requirements.map(([title, description]) => (
          <section key={title} className="rounded-lg border border-border bg-card p-5 panel-glow">
            <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
            <h2 className="mt-3 text-sm font-semibold">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
            <p className="mt-4 text-xs text-warning">
              Awaiting connected case evidence and assessment
            </p>
          </section>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to="/data-confidence" className="rounded border border-border px-4 py-2 text-sm">
          Review data confidence
        </Link>
        <Link
          to="/problem-records"
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Review Problem Records
        </Link>
      </div>
    </WorkspacePage>
  );
}

function EvidenceNote({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-center gap-2">
        <FileSearch className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{children}</p>
    </section>
  );
}
